import { Check, Copy, ExternalLink, MoreVertical, Plus, Trash } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { IconButton } from "src/components/ui/icon-button";
import GithubIcon from "src/components/ui/icons/github";
import { ErrorAlert } from "src/components/ui/status-alert";
import { Switch } from "src/components/ui/switch";
import { Tooltip } from "src/components/ui/tooltip";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { GitHubIntegrationsDialogCreateInstallationLinkMutation } from "./__generated__/GitHubIntegrationsDialogCreateInstallationLinkMutation.graphql";
import type { GitHubIntegrationsDialogDeleteIntegrationMutation } from "./__generated__/GitHubIntegrationsDialogDeleteIntegrationMutation.graphql";
import type { GitHubIntegrationsDialogQuery } from "./__generated__/GitHubIntegrationsDialogQuery.graphql";
import type { GitHubIntegrationsDialogUpdateSmartSyncMutation } from "./__generated__/GitHubIntegrationsDialogUpdateSmartSyncMutation.graphql";
import GithubIntegration from "./GithubIntegration";

interface GitHubIntegrationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const gitHubIntegrationsDialogQuery = graphql`
  query GitHubIntegrationsDialogQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: GITHUB) {
          edges {
            node {
              id
              name
              integrationType
              integrationStatus
              credentials
              createdAt
              smartSyncSettings {
                days
                status
                backfillError
              }
            }
          }
        }
      }
    }
  }
`;

const createGitHubInstallationLinkMutation = graphql`
  mutation GitHubIntegrationsDialogCreateInstallationLinkMutation($input: CreateGitHubInstallationLinkInput) {
    createGithubInstallationLink(input: $input) {
      installationLink
    }
  }
`;

const updateSmartSyncMutation = graphql`
  mutation GitHubIntegrationsDialogUpdateSmartSyncMutation($input: UpdateSmartSyncInput!) {
    updateSmartSync(input: $input) {
      integration {
        id
        smartSyncSettings {
          days
          status
          backfillError
        }
      }
      error
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation GitHubIntegrationsDialogDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

export default function GitHubIntegrationsDialog({ isOpen, onClose, onRefetch, brandTheme }: GitHubIntegrationsDialogProps) {
  const [isConnectingNew, setIsConnectingNew] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const [installationLink, setInstallationLink] = useState<string | null>(null);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    integration: any | null;
  }>({
    isOpen: false,
    integration: null,
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();

  const [commitCreateInstallationLink] = useMutation<GitHubIntegrationsDialogCreateInstallationLinkMutation>(
    createGitHubInstallationLinkMutation
  );

  const [commitDeleteIntegration] = useMutation<GitHubIntegrationsDialogDeleteIntegrationMutation>(
    deleteIntegrationMutation
  );

  const [commitUpdateSmartSync] = useMutation<GitHubIntegrationsDialogUpdateSmartSyncMutation>(
    updateSmartSyncMutation
  );

  const data = isOpen
    ? useLazyLoadQuery<GitHubIntegrationsDialogQuery>(
        gitHubIntegrationsDialogQuery,
        {},
        { fetchPolicy: "network-only" },
      )
    : null;

  const githubIntegrations = data?.me.organization?.integrations?.edges || [];

  const handleCopyLink = () => {
    // Generate the link if we don't have it yet
    if (!installationLink) {
      setIsCopyingLink(true);
      commitCreateInstallationLink({
        variables: { input: {} },
        onCompleted: (response) => {
          setIsCopyingLink(false);
          const link = response.createGithubInstallationLink?.installationLink;
          if (link) {
            setInstallationLink(link);
            navigator.clipboard.writeText(link).then(
              () => {
                setIsCopied(true);
                toast({
                  title: "Installation link copied to clipboard",
                  variant: "success",
                });

                setTimeout(() => {
                  setIsCopied(false);
                }, 2000);
              },
              () => {
                toast({
                  title: "Failed to copy link",
                  description: "Please try again",
                  variant: "error",
                });
              }
            );
          } else {
            toast({
              title: "Failed to generate installation link",
              description: "No link was returned from the server",
              variant: "error",
            });
          }
        },
        onError: (error) => {
          setIsCopyingLink(false);
          toast({
            title: "Failed to generate installation link",
            description: error.message,
            variant: "error",
          });
        },
      });
    } else {
      // Link already exists, just copy it
      navigator.clipboard.writeText(installationLink).then(
        () => {
          setIsCopied(true);
          toast({
            title: "Installation link copied to clipboard",
            variant: "success",
          });

          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        },
        () => {
          toast({
            title: "Failed to copy link",
            description: "Please try again",
            variant: "error",
          });
        }
      );
    }
  };

  const handleConnectAnother = () => {
    setIsConnectingNew(true);
    commitCreateInstallationLink({
      variables: { input: {} },
      onCompleted: (response) => {
        setIsConnectingNew(false);
        const link = response.createGithubInstallationLink?.installationLink;
        if (link) {
          // Navigate to GitHub installation
          window.location.href = link;
        } else {
          toast({
            title: "Failed to generate installation link",
            description: "No link was returned from the server",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsConnectingNew(false);
        toast({
          title: "Failed to create installation link",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleDeleteClick = (integration: any) => {
    setDeleteConfirmDialog({
      isOpen: true,
      integration,
    });
    setDeleteError(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmDialog.integration) {
      setDeleteError("Integration not found");
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    commitDeleteIntegration({
      variables: {
        input: {
          id: deleteConfirmDialog.integration.id,
        },
      },
      onCompleted: (response, errors) => {
        setIsDeleting(false);

        if (errors && errors.length > 0) {
          setDeleteError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setDeleteError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setDeleteConfirmDialog({ isOpen: false, integration: null });
          toast({
            title: "GitHub installation disconnected",
            variant: "success",
          });

          // Close dialog if no more installations
          if (githubIntegrations.length <= 1) {
            onClose();
          }

          onRefetch?.();
        }
      },
      onError: (error) => {
        setIsDeleting(false);
        setDeleteError(error.message);
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ isOpen: false, integration: null });
    setDeleteError(null);
  };

  const handleSmartSyncToggle = (integrationId: string, currentlyEnabled: boolean) => {
    commitUpdateSmartSync({
      variables: {
        input: {
          integrationId,
          enabled: !currentlyEnabled,
        },
      },
      onCompleted: (response) => {
        if (response.updateSmartSync.error) {
          toast({
            title: "Failed to update Efficient Sync",
            description: response.updateSmartSync.error,
            variant: "error",
          });
          return;
        }
        toast({
          title: !currentlyEnabled ? "Efficient Sync enabled" : "Efficient Sync disabled",
          variant: "success",
        });
        onRefetch?.();
      },
      onError: (error) => {
        toast({
          title: "Failed to update Efficient Sync",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  // If no installations, show the full GitHub connection flow
  if (githubIntegrations.length === 0) {
    return <GithubIntegration isOpen={isOpen} onClose={onClose} />;
  }

  // Otherwise, show the installations management dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-4xl bg-popover text-foreground border-border"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <GithubIcon className="h-6 w-6" />
            GitHub Installations
          </DialogTitle>
          <DialogDescription>
            Manage your GitHub App installations. You can connect multiple GitHub organizations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          {/* Installations Table */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Connected Installations</h3>

            {/* Header row */}
            <div className="grid grid-cols-[minmax(200px,2fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_40px] gap-4 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground">Organization Name</div>
              <div className="text-sm font-medium text-muted-foreground">Installation ID</div>
              <div className="text-sm font-medium text-muted-foreground">Connected</div>
              <div className="text-sm font-medium text-muted-foreground">Efficient Sync</div>
              <div></div>
            </div>

            {/* Installation rows */}
            {githubIntegrations.length > 0 && (
              githubIntegrations.map((edge): React.ReactNode => {
                const integration = edge.node;
                const credentials = integration.credentials as any;
                const accountName = credentials?.account_name || "Unknown";
                const installationId = credentials?.installation_id || "N/A";
                const createdAt = integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : "N/A";
                const needsApproval = credentials?.permissions_approval_needed === true;
                const smartSyncEnabled = !!integration.smartSyncSettings;

                return (
                  <div key={integration.id} className="grid grid-cols-[minmax(200px,2fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_40px] gap-4 items-start py-2">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-foreground font-medium truncate">{accountName}</span>
                      {needsApproval && (
                        <span className="text-xs bg-amber-900/30 text-amber-400 px-2 py-0.5 rounded border border-amber-700 w-fit">
                          Needs Approval
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground font-mono text-sm pt-0.5 min-w-0">{installationId}</div>
                    <div className="text-muted-foreground text-sm pt-0.5 min-w-0">{createdAt}</div>
                    <div className="pt-0.5">
                      <Tooltip
                        content="Only give the agent access to recently used repos"
                        position="top"
                      >
                        <Switch
                          checked={smartSyncEnabled}
                          onCheckedChange={() => handleSmartSyncToggle(integration.id, smartSyncEnabled)}
                          disabled={!canModify}
                        />
                      </Tooltip>
                    </div>
                    <div className="flex gap-2 items-start pt-0.5 justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <IconButton
                            icon={MoreVertical}
                            variant="none"
                            size="sm"
                            className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                            title="More options"
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-card border-border text-foreground">
                          <DropdownMenuItem
                            onClick={() => {
                              window.open(`https://github.com/settings/installations/${installationId}`, "_blank");
                            }}
                            className="hover:bg-accent cursor-pointer"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View on GitHub
                          </DropdownMenuItem>
                          <div>
                            <Tooltip
                              content={!canModify ? "You don't have permission to disconnect integrations" : null}
                              position="left"
                            >
                              <DropdownMenuItem
                                onClick={() => canModify && handleDeleteClick(integration)}
                                className="hover:bg-accent cursor-pointer text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!canModify}
                              >
                                <Trash className="h-4 w-4 mr-2" />
                                Disconnect
                              </DropdownMenuItem>
                            </Tooltip>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })
            )}

            {/* Action buttons section */}
            <div className="pt-4 border-t border-accent space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Install {productName} to your GitHub to enable repository access and commit analysis.
                </p>
                <Tooltip
                  content={!canModify ? "You don't have permission to connect integrations" : null}
                  position="top"
                >
                  <span className="inline-block">
                    <Button
                      onClick={handleConnectAnother}
                      disabled={isConnectingNew || !canModify}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {isConnectingNew ? "Loading..." : "Install on GitHub"}
                    </Button>
                  </span>
                </Tooltip>
              </div>

              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Share Installation Link</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Not a GitHub admin? Copy the installation link and share it with an admin who can install {productName}.
                </p>
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  disabled={isCopyingLink}
                  className="border-border hover:bg-card"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      {isCopyingLink ? "Generating..." : "Copy Install Link"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Delete confirmation dialog */}
      <UIConfirmationDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Disconnect GitHub Installation"
        confirmText={isDeleting ? "Disconnecting..." : "Disconnect"}
        cancelText="Cancel"
        variant="destructive"
        confirmDisabled={isDeleting}
      >
        <div className="space-y-3">
          <p>
            Are you sure you want to disconnect the GitHub installation for{" "}
            <span className="font-semibold">
              {deleteConfirmDialog.integration?.credentials?.account_name || "this account"}
            </span>
            ?
          </p>
          <p className="text-sm text-muted-foreground">
            This will remove access to all repositories from this installation. This action cannot be undone.
          </p>

          {deleteError && (
            <ErrorAlert icon="none">
              {deleteError}
            </ErrorAlert>
          )}
        </div>
      </UIConfirmationDialog>
    </Dialog>
  );
}
